import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePermissions } from './auth/usePermissions.js';
import { ORG } from './auth/capabilities.js';
import { DeniedState } from './auth/DeniedState.jsx';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TenancyModal } from '../platform/_ui';
import * as api from '../api/org-backup.api.js';

const fmt     = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const STATUS_TONE = { COMPLETED: 'green', RUNNING: 'blue', FAILED: 'red', PENDING: 'amber' };
const encryptionLabel = (s) => { if (s == null) { return '—'; } return s.encrypted ? 'AES-256' : 'Standard'; };

export default function OrgBackup({ data }) {
  const { orgId } = useParams();
  const navigate  = useNavigate();
  const [searchParams] = useSearchParams();
  const toast     = useToast();
  const perm      = usePermissions();

  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel');
  const [settings, setSettings] = useState(null);
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [acting, setActing]     = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);

  const org = data.state.organizations.find(o => o.id === orgId);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, h] = await Promise.all([api.getBackupSettings(orgId), api.getBackupHistory(orgId)]);
      setSettings(s);
      setHistory(Array.isArray(h) ? h : []);
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load backup data' });
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const handleTrigger = async () => {
    try {
      setActing(true);
      await api.triggerBackup(orgId);
      toast({ tone: 'success', icon: 'Check', title: 'Backup triggered', desc: 'Manual backup is running.' });
      load();
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Backup failed', desc: 'Could not trigger backup.' });
    } finally { setActing(false); }
  };

  const handleRestore = async () => {
    setConfirmRestore(false);
    try {
      setActing(true);
      await api.requestRestore(orgId);
      toast({ tone: 'success', icon: 'Check', title: 'Restore requested', desc: 'Your restore request has been submitted.' });
      load();
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Restore failed', desc: 'Could not submit restore request.' });
    } finally { setActing(false); }
  };

  if (!org) return null;

  const lastBackup   = history[0] ?? null;
  const successCount = history.filter(h => (h.status || '').toUpperCase() === 'COMPLETED').length;
  const failCount    = history.filter(h => (h.status || '').toUpperCase() === 'FAILED').length;

  const canTrigger  = perm.has(ORG.BACKUP_CREATE)  || perm.isSA;
  const canRestore  = perm.has(ORG.BACKUP_RESTORE) || perm.isSA;

  if (!perm.has(ORG.BACKUP_VIEW) && !perm.isSA) return (
    <div className="nx-cc-page">
      <DeniedState reason="insufficient_permission" message="Backup & Restore requires the Backup View capability. Contact your org admin." />
    </div>
  );

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Backup & Restore"
        sub={`Data protection and recovery settings for ${org.name}`}
        back={returnTo ? { label: returnLabel ? `Back to ${returnLabel}` : 'Back', onClick: () => navigate(returnTo) } : undefined}
        actions={
          (canTrigger || canRestore) ? (
            <div style={{ display: 'flex', gap: 8 }}>
              {canRestore  && <Btn variant="ghost"   icon="RotateCcw" disabled={acting} onClick={() => setConfirmRestore(true)}>Request Restore</Btn>}
              {canTrigger  && <Btn variant="primary"  icon="Download"  disabled={acting} onClick={handleTrigger}>{acting ? 'Running…' : 'Trigger Backup'}</Btn>}
            </div>
          ) : null
        }
      />

      {loading ? (
        <div className="nx-empty" style={{ padding: '48px 0' }}><NexusIcon name="Loader" size={20} /><span>Loading backup data…</span></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Stat tiles */}
          <div className="nx-cc-stat-grid nx-kpi-4">
            <StatCard icon="Clock"    label="Last Backup"      value={lastBackup ? fmtDate(lastBackup.createdAt) : 'Never'} sub={lastBackup?.status ? lastBackup.status.charAt(0).toUpperCase() + lastBackup.status.slice(1).toLowerCase() : '—'} color={lastBackup?.status?.toUpperCase() === 'FAILED' ? 'red' : 'green'} />
            <StatCard icon="Calendar" label="Schedule"         value={settings?.backupSchedule ? settings.backupSchedule.charAt(0).toUpperCase() + settings.backupSchedule.slice(1) : '—'} sub="automated cadence" color="blue" />
            <StatCard icon="Archive"  label="Retention Period" value={settings?.backupRetention != null ? `${settings.backupRetention} days` : '—'} sub="backup window" color="purple" />
            <StatCard icon="CheckCircle" label="Successful"    value={successCount} sub={failCount > 0 ? `${failCount} failed` : 'no failures'} color={failCount > 0 ? 'amber' : 'green'} />
          </div>

          {/* Config row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div className="nx-cc-chart-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Backup Configuration</div>
              {[
                { label: 'Schedule',    value: settings?.backupSchedule ?? '—',    icon: 'Clock'    },
                { label: 'Retention',   value: settings?.backupRetention != null ? `${settings.backupRetention} days` : '—', icon: 'Archive' },
                { label: 'Encryption',  value: encryptionLabel(settings), icon: 'Lock'   },
                { label: 'Storage',     value: settings?.storageRegion ?? '—',    icon: 'HardDrive'},
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--nx-divider)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <NexusIcon name={row.icon} size={13} style={{ color: 'var(--nx-text-muted)' }} />
                  </div>
                  <span style={{ flex: 1, fontSize: 12, color: 'var(--nx-text-muted)' }}>{row.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{row.value}</span>
                </div>
              ))}
              <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 8, background: 'var(--nx-accent-tint)', fontSize: 12, color: 'var(--nx-text-muted)', display: 'flex', gap: 8 }}>
                <NexusIcon name="Info" size={13} style={{ color: 'var(--nx-accent)', flexShrink: 0, marginTop: 1 }} />
                <span>Contact your account manager to modify the backup schedule or retention policy.</span>
              </div>
            </div>

            <div className="nx-cc-chart-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Quick Actions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ padding: 16, borderRadius: 8, border: '1.5px solid var(--nx-border)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--nx-accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <NexusIcon name="Download" size={16} style={{ color: 'var(--nx-accent)' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Manual Backup</div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 10 }}>Capture a full snapshot of all organization data right now.</div>
                    {canTrigger && (
                      <Btn variant="primary" size="sm" icon="Download" disabled={acting} onClick={handleTrigger}>
                        {acting ? 'Running…' : 'Run Now'}
                      </Btn>
                    )}
                  </div>
                </div>

                <div style={{ padding: 16, borderRadius: 8, border: '1.5px solid var(--nx-border)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--nx-warning-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <NexusIcon name="RotateCcw" size={16} style={{ color: 'var(--nx-warning)' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Request Restore</div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 10 }}>Submit a restore request. Your account manager will assist.</div>
                    {canRestore && (
                      <Btn variant="ghost" size="sm" icon="RotateCcw" disabled={acting} onClick={() => setConfirmRestore(true)}>Request</Btn>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* History table */}
          <div className="nx-cc-chart-card" style={{ padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Backup History</div>
            {history.length === 0 ? (
              <div className="nx-empty" style={{ padding: '32px 0' }}>
                <NexusIcon name="Archive" size={24} style={{ color: 'var(--nx-text-faint)' }} />
                <span>No backup history yet. Trigger a backup to get started.</span>
              </div>
            ) : (
              <table className="nx-table">
                <thead><tr><th>Date &amp; Time</th><th>Type</th><th>Status</th><th>Note</th></tr></thead>
                <tbody>
                  {history.map((h, i) => {
                    const st = (h.status || 'COMPLETED').toUpperCase();
                    return (
                      <tr key={h.id ?? i}>
                        <td style={{ fontSize: 12 }}>{fmt(h.createdAt)}</td>
                        <td style={{ fontSize: 12 }}><Pill tone="gray">{h.action ?? 'Backup'}</Pill></td>
                        <td><Pill tone={STATUS_TONE[st] || 'gray'}>{h.status ?? 'Completed'}</Pill></td>
                        <td style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{h.note ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {confirmRestore && (
        <TenancyModal title="Request Restore?" icon="RotateCcw" onClose={() => setConfirmRestore(false)}>
          <p className="nx-confirm-body">This will submit a restore request to your Nuacem account manager. Are you sure you want to continue?</p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setConfirmRestore(false)}>Cancel</Btn>
            <Btn variant="danger" icon="RotateCcw" disabled={acting} onClick={handleRestore}>Request Restore</Btn>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}
